import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ClinicalPageShell,
  SignalBar,
  SectionPanel,
  ClinicalDataGrid,
  tokens,
  fmtNumber,
} from "../../shared/clinical-design";
import ProviderScorecardPanel from "../components/ProviderScorecardPanel";
import { getProviderPerformance } from "../services/providerPerformanceService";

/** Celda % con tono según umbrales (más alto = mejor). */
const PctCell = ({ value, warnBelow, critBelow }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: tokens.fontMono,
      fontVariantNumeric: "tabular-nums",
      fontSize: 13,
      color: value < critBelow ? tokens.crit : value < warnBelow ? tokens.warn : tokens.ink,
    }}
  >
    {value.toFixed(1)}%
  </Typography>
);

const providerColumns = [
  { key: "name", header: "Provider" },
  { key: "center", header: "Center" },
  {
    key: "panelSize",
    header: "Panel",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.panelSize),
  },
  {
    key: "rafScore",
    header: "RAF",
    align: "right",
    mono: true,
    render: (r) => r.rafScore.toFixed(2),
  },
  {
    key: "codingSpecificity",
    header: "Specificity",
    align: "right",
    render: (r) => <PctCell value={r.codingSpecificity} warnBelow={75} critBelow={65} />,
  },
  {
    key: "chargeLagDays",
    header: "Charge lag",
    align: "right",
    render: (r) => (
      <Typography
        component="span"
        sx={{
          fontFamily: tokens.fontMono,
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
          color: r.chargeLagDays > 7 ? tokens.crit : r.chargeLagDays > 4.5 ? tokens.warn : tokens.ink,
        }}
      >
        {r.chargeLagDays.toFixed(1)}d
      </Typography>
    ),
  },
  {
    key: "gapClosureRate",
    header: "Gap closure",
    align: "right",
    render: (r) => <PctCell value={r.gapClosureRate} warnBelow={60} critBelow={50} />,
  },
  {
    key: "visitsPerDay",
    header: "Visits/day",
    align: "right",
    mono: true,
    render: (r) => r.visitsPerDay.toFixed(1),
  },
];

/**
 * Module M — Provider Performance & Scorecards.
 * Lista de PCPs (master) + scorecard individual vs peer interno (detail).
 * Refuerza el coaching de coding (Módulo F) con datos comparables.
 */
export default function ProviderPerformancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getProviderPerformance();
        if (active) {
          setData(result);
          // Selecciona el primer provider para que el detail nunca arranque vacío.
          setSelectedId(result.providers[0]?.id ?? null);
        }
      } catch (e) {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const s = data?.signals;
  const signals = s
    ? [
        { label: "Providers", value: fmtNumber(s.providerCount) },
        {
          label: "Avg RAF",
          value: s.avgRaf.toFixed(2),
          tone: "info",
          hint: "Panel-weighted group Risk Adjustment Factor",
        },
        {
          label: "Coding specificity",
          value: `${s.avgSpecificity.toFixed(1)}%`,
          tone: s.avgSpecificity < 80 ? "warn" : "ok",
          hint: "Group average — diagnoses coded to highest specificity",
        },
        {
          label: "Gap closure",
          value: `${s.avgGapClosure.toFixed(1)}%`,
          tone: s.avgGapClosure < 65 ? "warn" : "ok",
        },
        {
          label: "Avg charge lag",
          value: `${s.avgChargeLag.toFixed(1)}d`,
          tone: s.avgChargeLag > 4.5 ? "warn" : "ok",
          hint: "Days between service and posted charge",
        },
      ]
    : [];

  const selectedProvider =
    data?.providers.find((p) => p.id === selectedId) || null;

  return (
    <ClinicalPageShell
      title="Provider Performance"
      subtitle="PCP scorecards against the internal peer average: where each provider captures well and where HCCs and gaps are being left behind."
      moduleTag="Module M"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 2 }} />

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <SectionPanel title="Providers" contentSx={{ p: 0 }} sx={{ flex: 7, minWidth: 0 }}>
          <ClinicalDataGrid
            columns={providerColumns}
            rows={data?.providers || []}
            rowKey={(r) => r.id}
            onRowClick={(r) => setSelectedId(r.id)}
            isRowSelected={(r) => r.id === selectedId}
            loading={loading}
            emptyMessage="No provider scorecards available for this period."
            maxHeight={560}
            sx={{ border: 0 }}
          />
        </SectionPanel>
        <SectionPanel title="Scorecard vs peer" sx={{ flex: 5, minWidth: 0 }}>
          <ProviderScorecardPanel
            provider={selectedProvider}
            peerAverages={data?.peerAverages}
          />
        </SectionPanel>
      </Stack>
      <Box sx={{ height: 8 }} />
    </ClinicalPageShell>
  );
}
